import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import Select from "react-select";
import imageCustomSquare from "assets/images/imagecustomrounded.png";
import {
  validateSearchBrokerageAgentTeamSelection,
} from "../validationSchemas/searchBrokerageAgentTeam";
import {
  appendPermissionsFalse,
  resolveOfficeSelectBrokerageOrganizationId,
  fetchOfficeTeamMembersSelect2,
  fetchBrokerageAgentsByOfficeIdsSelect2,
  fetchBrokerageTeamMemberOfficesSelect2,
} from "api/salesOrderApi";
import { mapFoundationMembershipRowToOption } from "../utils/orderPartyIdentity";
import "./SearchBrokerageAgentTeam.css";

/** Prevent infinite onError loop when fallback image also fails to load. */
function handleProfileImageError(event) {
  const img = event.currentTarget;
  if (img.dataset.fallbackApplied === "1") {
    img.onerror = null;
    return;
  }
  img.onerror = null;
  img.dataset.fallbackApplied = "1";
  img.src = imageCustomSquare;
}

const API_BASE = process.env.REACT_APP_API_URL || "";
const DEFAULT_LIMIT = 10;
const DEBOUNCE_MS = 300;

// License type from API → section header label
const SECTION_HEADERS = {
  BROKERAGE_FIRM: "Brokerages",
  OFFICE: "Offices",
  BROKERAGE_AGENT: "Brokerages Agents",
  AGENT: "Independent Agents",
  BROKERAGE_TEAM_MEMBER: "Brokerages Team Members",
  TEAM_MEMBER: "Team Members",
};

// License type → badge label and CSS class
const BADGE_MAP = {
  BROKERAGE_FIRM: { label: "Brokerage", cls: "search-bat-badge search-bat-badge-b" },
  OFFICE: { label: "Office", cls: "search-bat-badge search-bat-badge-o" },
  // BROKERAGE_AGENT: { label: "Brokerage Agent", cls: "search-bat-badge search-bat-badge-ba" },
  BROKERAGE_AGENT: { label: "Agent", cls: "search-bat-badge search-bat-badge-ba" },
  AGENT: { label: "Agent", cls: "search-bat-badge search-bat-badge-a" },
  BROKERAGE_TEAM_MEMBER: { label: "Team Member", cls: "search-bat-badge search-bat-badge-m" },
  TEAM_MEMBER: { label: "Team Member", cls: "search-bat-badge search-bat-badge-m" },
};

// Order of sections in dropdown
const SECTION_ORDER = [
  "BROKERAGE_FIRM",
  "OFFICE",
  "AGENT",
  "BROKERAGE_TEAM_MEMBER",
  "TEAM_MEMBER",
];

/**
 * Canonical license key for grouping, badge, and subtitles.
 * - Normalizes casing (API may send "office", "Office", etc.)
 * - Office entity rows often have value === offices._id even if license_type is wrong/missing
 */
function getEffectiveLicenseType(item) {
  if (!item) return "AGENT";
  const raw = String(item.license_type ?? "").trim();
  const u = raw.toUpperCase();
  if (u === "OFFICE") return "OFFICE";
  const val = String(item.value ?? item.id ?? item._id ?? "").trim();
  const off = item.offices;
  if (off && typeof off === "object") {
    const oid = String(off._id ?? off.id ?? "").trim();
    if (oid && val && oid === val) return "OFFICE";
  }
  if (u === "BROKERAGE_AGENT") return "AGENT";
  if (SECTION_ORDER.includes(u)) return u;
  return u || "AGENT";
}

/**
 * Get meta subtitle for a row (left side under label).
 * - BROKERAGE_FIRM: "X offices"
 * - BROKERAGE_TEAM_MEMBER / TEAM_MEMBER: brokerage name or "Team Member"
 * - BROKERAGE_AGENT: brokerage name or "Brokerage Agent"
 * - AGENT: signage_firm_name or "Independent Agent"
 */
/** Subtitle for OFFICE rows: address + brokerage (license type is shown in the badge). */
function getOfficeMeta(item) {
  const off = item.offices;
  if (!off || typeof off !== "object") {
    return item.company_name || "";
  }
  const addrs = off.officeAddress;
  const first = Array.isArray(addrs) && addrs.length ? addrs[0] : null;
  const companyName = item.company_name || item.brokerage_name || "";
  const parts = [];
  if (first) {
    const line1 = first.addressLine1 ? String(first.addressLine1).trim() : "";
    const cityState = [first.city, first.state].filter(Boolean).join(", ");
    const country = first.country ? String(first.country).trim() : "";
    const addrBits = [line1, cityState, country].filter(Boolean).join(" · ");
    if (addrBits) parts.push(addrBits);
  }
  if (companyName) parts.push(companyName);
  if (parts.length) return parts.join(" · ");
  return "";
}

function getMeta(item) {
  const lt = getEffectiveLicenseType(item);
  const officeCount = item.office_count ?? item.office_count;
  const companyName = String(item.company_name || item.brokerage_name || "").trim() || "-";
  const officeName = String(
    item.officeName ||
      item.office_name ||
      item?.offices?.officeName ||
      ""
  ).trim() || "-";
  if (lt === "OFFICE") {
    const detail = getOfficeMeta(item);
    return detail || "—";
  }
  if (lt === "BROKERAGE_FIRM" && (officeCount !== undefined && officeCount !== null)) {
    return `${Number(officeCount)} offices`;
  }
  if (lt === "BROKERAGE_TEAM_MEMBER" || lt === "TEAM_MEMBER") {
    const teamOfficeCount = item.office_count ?? item.offices_count;
    const officeCountText =
      teamOfficeCount !== undefined &&
      teamOfficeCount !== null &&
      String(teamOfficeCount).trim() !== ""
        ? String(teamOfficeCount).trim()
        : "-";
    return `${companyName}, ${officeCountText}`;
  }
  if (lt === "BROKERAGE_AGENT") {
    return `${companyName}, ${officeName}`;
  }
  if (lt === "AGENT") {
    return `${companyName}, ${officeName}`;
  }
  return "—";
}

function isMeaningfulMeta(value) {
  const v = String(value || "").trim();
  return v !== "" && v !== "—" && v !== "-" && v !== "-, -" && v !== "-,-";
}

function getMetaLines(item) {
  const lt = getEffectiveLicenseType(item);
  const companyName = String(item?.company_name || item?.brokerage_name || "").trim() || "-";
  const officeName = String(
    item?.officeName ||
      item?.office_name ||
      item?.offices?.officeName ||
      ""
  ).trim() || "-";
  const teamOfficeCountRaw = item?.office_count ?? item?.offices_count;
  const teamOfficeCountNumber =
    teamOfficeCountRaw !== undefined &&
    teamOfficeCountRaw !== null &&
    String(teamOfficeCountRaw).trim() !== ""
      ? Number(teamOfficeCountRaw)
      : NaN;
  const teamOfficeCount =
    Number.isFinite(teamOfficeCountNumber) && teamOfficeCountNumber >= 0
      ? `${Math.floor(teamOfficeCountNumber)} office${Math.floor(teamOfficeCountNumber) === 1 ? "" : "s"}`
      : "-";

  if (lt === "BROKERAGE_AGENT" || lt === "AGENT") {
    return [companyName, officeName];
  }
  if (lt === "BROKERAGE_TEAM_MEMBER" || lt === "TEAM_MEMBER") {
    return [companyName, teamOfficeCount];
  }
  return null;
}

/** Email line below meta; API may send `user@x.com||admin` — show only the address part. */
function formatEmailForRow(email) {
  if (email == null || String(email).trim() === "") return "—";
  const first = String(email).trim().split("||")[0].trim();
  return first || "—";
}

function firstOfficeFromSearchItem(item) {
  if (!item) return null;
  if (Array.isArray(item.offices)) return item.offices[0] || null;
  if (item.offices && typeof item.offices === "object") return item.offices;
  return null;
}

function resolveOfficeIdFromSearchItem(item, normalized) {
  const nested = firstOfficeFromSearchItem(item);
  return String(
    nested?._id ||
      nested?.id ||
      item?.office_id ||
      item?.officeId ||
      normalized?.value ||
      ""
  ).trim();
}

function resolveOfficeLabelFromSearchItem(item, fallback) {
  const nested = firstOfficeFromSearchItem(item);
  return String(
    nested?.officeName ||
      nested?.name ||
      item?.office_name ||
      item?.officeName ||
      fallback ||
      "Office"
  ).trim();
}

function getPrimaryDisplayText(item) {
  if (!item) return "";
  const lt = getEffectiveLicenseType(item);
  // For brokerage firms, show the company name in the UI (but keep label for contact name).
  if (lt === "BROKERAGE_FIRM" && item.company_name) return item.company_name;
  if (lt === "OFFICE") {
    return resolveOfficeLabelFromSearchItem(item, item.label || "");
  }
  return item.label || "";
}

function getOfficeAvailableCount(item) {
  if (!item) return 0;
  const count = Number(item.office_count ?? item?.offices_count ?? item?.available_office_count ?? 0);
  if (Number.isFinite(count) && count > 0) return count;
  return 1;
}

/** Prefer API `phone1`, then `phone`, for user / search rows. */
function resolvePrimaryPhoneFromUserLike(obj) {
  if (!obj || typeof obj !== "object") return "";
  const p1 = obj.phone1 != null ? String(obj.phone1).trim() : "";
  if (p1) return p1;
  const p2 = obj.phone != null ? String(obj.phone).trim() : "";
  return p2 || "";
}

/** Normalize API item so parent always receives membership-based value + label. */
function normalizeItemForPayload(item) {
  if (!item) return item;
  const membershipId = String(
    item.membership_id ?? item.membershipId ?? item.value ?? item.id ?? item._id ?? ""
  ).trim();
  const globalUserId = String(item.global_user_id ?? item.globalUserId ?? "").trim();
  const organizationId = String(item.organization_id ?? item.organizationId ?? "").trim();
  const label =
    item.label ??
    item.name ??
    (item.firstName != null || item.lastName != null
      ? `${item.firstName || ""} ${item.lastName || ""}`.trim()
      : "");
  return {
    ...item,
    value: membershipId,
    membershipId,
    globalUserId,
    organizationId,
    addressOwnerId: organizationId || membershipId,
    label: label || item.label || item.name || "",
  };
}

/**
 * SearchBrokerageAgentTeam – API-driven search for Create Order / Add Installation.
 * Fetches from organizations/:organizationId/created-or-linked-detailed.
 * Groups by license_type: Brokerages, Brokerages Agents, Independent Agents, Brokerages Team Members, Team Members.
 * Left: profile_pic_url / logo_url (or default image), label, meta. Right: license type badge.
 *
 * @param {Object} props
 * @param {string} props.organizationId - Seller organization ID for API
 * @param {string} props.token - Auth token
 * @param {string} [props.title]
 * @param {string} [props.subtitle]
 * @param {Function} [props.onContinue] - (payload) => {} with { type, item, contact }
 * @param {Function} [props.onContactChange] - (contact) => {} syncs external form fields
 * @param {Function} [props.onOfficeChange] - (office) => {} when office selected
 * @param {Function} [props.onTeamMemberChange] - (member) => {} when team member selected
 * @param {Function} [props.onReset] - () => {}
 * @param {'default'|'projectCreate'} [props.uiVariant] - projectCreate: flat layout, no inner label (parent provides).
 * @param {string} [props.searchPlaceholder] - Overrides default search input placeholder.
 * @param {boolean} [props.emailPickerMode] - Search + pick only (hide office/team/contact detail panels). For Send Quote/Invoice CC.
 */
const SearchBrokerageAgentTeam = forwardRef(function SearchBrokerageAgentTeam(
  {
    organizationId,
    token,
    title = "Create Order",
    subtitle = "Search for a brokerage, team member, or agent to begin",
    onContinue,
    onContactChange,
    onOfficeChange,
    onTeamMemberChange,
    onAgentChange,
    emailError = "",
    phoneError = "",
    officeError = "",
    teamMemberError = "",
    onReset,
    readOnly = false,
    contactReadOnly = false,
    contactNameReadOnly = true,
    initialLabel,
    initialContact,
    initialType,
    initialItem,
    uiVariant = "default",
    searchPlaceholder: searchPlaceholderProp,
    emailPickerMode = false,
  },
  ref
) {
  const DEFAULT_TEN_ZEROS = "0000000000";
  /** API/submit boundary — only when a non-empty phone is required downstream. */
  const applyDefaultPhoneIfEmpty = (v) => {
    const s = String(v ?? "").trim();
    return s || DEFAULT_TEN_ZEROS;
  };
  /** Input display: keep empty empty so users can select all and backspace before typing. */
  const phoneForInput = (v) => {
    const s = String(v ?? "").trim();
    return s;
  };

  const isProjectCreateUi = uiVariant === "projectCreate";
  const searchInputPlaceholder =
    searchPlaceholderProp ||
    (isProjectCreateUi
      ? "Search by name, brokerage, or office…"
      : "Search Brokerage Name, Admin Name, Agent Name or office Name. ");

  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  /** Bumps on each Enter so a refetch runs even when `searchQuery` text is unchanged. */
  const [searchRequestNonce, setSearchRequestNonce] = useState(0);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [hintsVisible, setHintsVisible] = useState(true);
  const [selection, setSelection] = useState(null); // { item } from API
  const [contact, setContact] = useState(
    initialContact || { fName: "", lName: "", email: "", phone: "" }
  );
  const [apiResults, setApiResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [offices, setOffices] = useState([]);
  const [officeValue, setOfficeValue] = useState("");
  const officeValueRef = useRef("");
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamMemberValue, setTeamMemberValue] = useState("");
  const teamMemberValueRef = useRef("");
  const [agents, setAgents] = useState([]);
  const [agentValue, setAgentValue] = useState("");
  const [agentQuery, setAgentQuery] = useState("");
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({ office: "", teamMember: "" });
  const dropdownRef = useRef(null);
  const dropdownListRef = useRef(null);
  const debounceRef = useRef(null);
  const latestRequestRef = useRef(0);
  const fetchAbortRef = useRef(null);
  const onOfficeChangeRef = useRef(onOfficeChange);
  const onTeamMemberChangeRef = useRef(onTeamMemberChange);
  const onAgentChangeRef = useRef(onAgentChange);
  const onContactChangeRef = useRef(onContactChange);
  useEffect(() => {
    onOfficeChangeRef.current = onOfficeChange;
    onTeamMemberChangeRef.current = onTeamMemberChange;
    onAgentChangeRef.current = onAgentChange;
    onContactChangeRef.current = onContactChange;
  }, [onOfficeChange, onTeamMemberChange, onAgentChange, onContactChange]);
  useEffect(() => {
    officeValueRef.current = String(officeValue || "").trim();
  }, [officeValue]);
  useEffect(() => {
    teamMemberValueRef.current = String(teamMemberValue || "").trim();
  }, [teamMemberValue]);

  useEffect(
    () => () => {
      if (fetchAbortRef.current) {
        fetchAbortRef.current.abort();
        fetchAbortRef.current = null;
      }
    },
    []
  );

  const fetchUsers = useCallback(
    async (query = "", pageNumber = 1, limit = DEFAULT_LIMIT, append = false) => {
      if (readOnly) {
        return;
      }
      if (!organizationId || !token || !API_BASE) {
        setApiResults([]);
        return;
      }
      const requestId = ++latestRequestRef.current;
      if (fetchAbortRef.current) {
        fetchAbortRef.current.abort();
      }
      const ac = new AbortController();
      fetchAbortRef.current = ac;

      setLoading(true);
      try {
        const params = new URLSearchParams({
          query: query.trim(),
          page: String(pageNumber),
          limit: String(limit),
        });
        const url = `${API_BASE}/organizations/${organizationId}/created-or-linked-detailed?${params}`;
        const res = await fetch(url, {
          signal: ac.signal,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        if (!res.ok) throw new Error(res.statusText);
        const json = await res.json();
        // Ignore stale responses so old searches never overwrite latest Enter search.
        if (requestId !== latestRequestRef.current) return;
        const data = Array.isArray(json?.data) ? json.data : [];
        setApiResults((prev) => (append ? [...prev, ...data] : data));

        const currentPage = Number(json?.page ?? pageNumber);
        const totalPages = Number(json?.totalPages ?? currentPage);
        setPage(currentPage);
        setHasMore(currentPage < totalPages && data.length >= limit);
      } catch (err) {
        if (err?.name === "AbortError") return;
        console.error("SearchBrokerageAgentTeam fetch error:", err);
        if (requestId !== latestRequestRef.current) return;
        setApiResults([]);
      } finally {
        if (requestId === latestRequestRef.current) {
          setLoading(false);
        }
      }
    },
    [organizationId, token, readOnly]
  );

  // Initial load: 10 records when organizationId/token available
  useEffect(() => {
    if (!organizationId || !token || readOnly) return;
    setPage(1);
    setHasMore(true);
    fetchUsers("", 1, DEFAULT_LIMIT, false);
  }, [organizationId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Search when user confirms (Enter): `searchQuery` + `searchRequestNonce` (single fetch; previous aborted in fetchUsers).
  useEffect(() => {
    if (!organizationId || !token || readOnly) return;
    const normalizedQuery = String(searchQuery || "").trim();
    setPage(1);
    setHasMore(true);
    fetchUsers(normalizedQuery, 1, DEFAULT_LIMIT, false);
  }, [searchQuery, searchRequestNonce, organizationId, token, fetchUsers, readOnly]);

  const setQuery = (v) => {
    if (readOnly) return;
    setSearchInput(v);
    setDropdownVisible(true);
  };

  const handleSearchChange = (e) => {
    if (readOnly) return;
    setSearchInput(e.target.value);
    setDropdownVisible(true);
  };

  const handleSearchKeyDown = (e) => {
    if (readOnly) return;
    if (e.key === "Enter") {
      e.preventDefault();
      const q = String(searchInput || "").trim();
      setSearchQuery(q);
      setSearchRequestNonce((n) => n + 1);
      setPage(1);
      setHasMore(true);
      setDropdownVisible(true);
    }
  };

  const handleSearchFocus = () => {
    if (readOnly) return;
    // If we have no results loaded (e.g., after clearing selection), fetch the first page.
    if (apiResults.length === 0 && !loading) {
      setPage(1);
      setHasMore(true);
      fetchUsers("", 1, DEFAULT_LIMIT, false);
    }
    setDropdownVisible(true);
  };

  const pick = (item) => {
    setDropdownVisible(false);
    setHintsVisible(false);
    const normalized = normalizeItemForPayload(item);

    const contactName = normalized.label || item.label || "";
    const effLt = getEffectiveLicenseType(item);
    const displayLabel =
      effLt === "BROKERAGE_FIRM" && item.company_name
        ? item.company_name
        : effLt === "OFFICE"
          ? item.offices?.officeName || contactName
          : contactName;

    const selectionItem = { ...normalized, displayLabel };
    setSelection({ item: selectionItem });
    setSearchInput(displayLabel);
    setFieldErrors({ office: "", teamMember: "" });
    // reset office / member on new selection
    setOfficeValue("");
    setTeamMemberValue("");
    setOffices([]);
    setTeamMembers([]);
    setAgentValue("");
    setAgentQuery("");
    setAgents([]);
    onAgentChangeRef.current?.(null);
    // Contact fields should come from the person's name (label), not the company name.
    const parts = contactName.split(" ");
    const shouldPrefillContact =
      effLt === "AGENT" ||
      effLt === "BROKERAGE_AGENT" ||
      effLt === "BROKERAGE_TEAM_MEMBER" ||
      effLt === "TEAM_MEMBER";
    const nextContact = shouldPrefillContact
      ? {
          fName: parts[0] || item.firstName || "",
          lName: parts.slice(1).join(" ") || item.lastName || "",
          email: item.email || "",
          phone: applyDefaultPhoneIfEmpty(resolvePrimaryPhoneFromUserLike(item)),
        }
      : {
          fName: "",
          lName: "",
          email: "",
          phone: DEFAULT_TEN_ZEROS,
        };
    setContact(nextContact);
    if (typeof onContactChange === "function") onContactChange(nextContact);
    if (typeof onContinue === "function") {
      onContinue({ type: effLt, item: normalized, contact: nextContact });
    }

    if (effLt === "OFFICE") {
      const officeId = resolveOfficeIdFromSearchItem(item, normalized);
      const officeLabel = resolveOfficeLabelFromSearchItem(
        item,
        displayLabel || normalized.label || "Office"
      );
      const officePayload = {
        id: officeId,
        label: officeLabel,
        company_name: item?.company_name || item?.brokerage_name || "",
        office_count: getOfficeAvailableCount(item),
      };
      if (officeId) {
        setOffices([{ id: officePayload.id, label: officePayload.label }]);
        setOfficeValue(officePayload.id);
      }
      if (officeId && typeof onOfficeChange === "function") {
        onOfficeChange(officePayload);
      }
    } else if (effLt === "BROKERAGE_TEAM_MEMBER" || effLt === "TEAM_MEMBER") {
      const rawOffices = Array.isArray(item?.offices)
        ? item.offices
        : item?.offices && typeof item.offices === "object"
          ? [item.offices]
          : [];
      const officeOpts = rawOffices
        .map((o) => ({
          id: String(o?.id || o?._id || "").trim(),
          label: String(o?.officeName || o?.name || "").trim() || "Office",
        }))
        .filter((o) => o.id);
      if (officeOpts.length > 0) {
        setOffices(officeOpts);
        setOfficeValue(officeOpts[0].id);
        if (typeof onOfficeChange === "function") {
          onOfficeChange({
            id: officeOpts[0].id,
            label: officeOpts[0].label,
            company_name: item?.company_name || item?.brokerage_name || "",
            office_count: officeOpts.length,
          });
        }
      }
    } else if (effLt === "BROKERAGE_AGENT" || effLt === "AGENT") {
      const rawAgentOffices = Array.isArray(item?.offices)
        ? item.offices
        : item?.offices && typeof item.offices === "object"
          ? [item.offices]
          : [];
      const agentOfficeOpts = rawAgentOffices
        .map((o) => ({
          id: String(o?.id || o?._id || "").trim(),
          label:
            String(o?.officeName || o?.name || item?.officeName || item?.office_name || "").trim() ||
            "Office",
        }))
        .filter((o) => o.id);
      if (agentOfficeOpts.length > 0) {
        setOffices(agentOfficeOpts);
        setOfficeValue(agentOfficeOpts[0].id);
        if (typeof onOfficeChange === "function") {
          onOfficeChange({
            id: agentOfficeOpts[0].id,
            label: agentOfficeOpts[0].label,
            company_name:
              item?.company_name || item?.brokerage_name || item?.brokerage_firm_name || "",
            office_count: agentOfficeOpts.length,
          });
        }
      }
    }
  };

  const pickLatestRef = useRef(null);
  pickLatestRef.current = pick;

  const clearAll = () => {
    if (readOnly) return;
    setSelection(null);
    setSearchInput("");
    setSearchQuery("");
    // Reset paging/infinite-scroll state so scroll loads more again after removal.
    setApiResults([]);
    setPage(1);
    setHasMore(true);
    setDropdownVisible(false);
    const empty = { fName: "", lName: "", email: "", phone: "" };
    setContact(empty);
    setOfficeValue("");
    setTeamMemberValue("");
    setAgentValue("");
    setAgentQuery("");
    setAgents([]);
    onAgentChangeRef.current?.(null);
    setFieldErrors({ office: "", teamMember: "" });
    if (typeof onContactChange === "function") onContactChange(empty);
    setHintsVisible(true);
    onReset?.();

    // Reload first page of results for empty query so dropdown can paginate correctly.
    // (Initial-load effect only runs on mount; clearAll can otherwise leave hasMore/page stale.)
    if (organizationId && token && API_BASE && typeof fetchUsers === "function") {
      fetchUsers("", 1, DEFAULT_LIMIT, false);
    }
  };

  const handleDropdownScroll = (e) => {
    if (readOnly) return;
    const el = e?.target || dropdownListRef.current;
    if (!el || loading || !hasMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchUsers(searchInput.trim(), nextPage, DEFAULT_LIMIT, true);
    }
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        const input = document.getElementById("search-bat-input");
        if (input && !input.contains(e.target)) setDropdownVisible(false);
      }
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  // Seed initial selection/contact (read-only or editable prefill)
  useEffect(() => {
    if (selection) return;
    const baseLabel =
      initialLabel ||
      `${initialContact?.fName || ""} ${initialContact?.lName || ""}`.trim() ||
      "";
    if (!baseLabel) return;

    const displayLabel = initialType === "BROKERAGE_FIRM" ? baseLabel : undefined;
    const source = initialItem && typeof initialItem === "object" ? initialItem : {};
    const companyName = String(
      source.company_name || source.brokerage_name || source.brokerage_firm_name || ""
    ).trim();
    const officeName = String(
      source.officeName || source.office_name || source.offices?.officeName || ""
    ).trim();
    const item = {
      ...source,
      value: source.value || source.membershipId || source.membership_id || "",
      label: baseLabel || source.label || "",
      ...(displayLabel ? { displayLabel } : source.displayLabel ? { displayLabel: source.displayLabel } : {}),
      email: initialContact?.email || source.email || "",
      phone: applyDefaultPhoneIfEmpty(
        resolvePrimaryPhoneFromUserLike(initialContact) || initialContact?.phone || source.phone
      ),
      license_type: initialType || source.license_type || "AGENT",
      ...(companyName
        ? {
            company_name: companyName,
            brokerage_name: companyName,
            brokerage_firm_name: companyName,
          }
        : {}),
      ...(officeName ? { officeName, office_name: officeName } : {}),
    };
    const parts = baseLabel.split(" ");
    const nextContact = {
      fName: initialContact?.fName || parts[0] || "",
      lName: initialContact?.lName || parts.slice(1).join(" ") || "",
      email: item.email || "",
      phone: item.phone,
    };
    setSelection({ item });
    setSearchInput(baseLabel);
    setContact(nextContact);
    onContactChangeRef.current?.(nextContact);
  }, [readOnly, selection, initialLabel, initialContact, initialType, initialItem]);

  const selectedItem = selection?.item || null;
  // Only lock when the parent asks (e.g. true read-only display). Do not lock just because
  // `initialLabel` is set: parents echo the chosen label back after onContinue, which would
  // incorrectly freeze the field and block picking a different user on create/edit flows.
  // const isSearchLocked = readOnly || (Boolean(initialLabel) && Boolean(selectedItem));
  const isSearchLocked = Boolean(readOnly);
  const selectedLicenseType = getEffectiveLicenseType(selectedItem);
  const isBrokerageFirm = selectedLicenseType === "BROKERAGE_FIRM";
  const isBrokerageTeamMember =
    selectedLicenseType === "BROKERAGE_TEAM_MEMBER" || selectedLicenseType === "TEAM_MEMBER";
  const isOfficeSelection = selectedLicenseType === "OFFICE";
  const showBrokerageCard =
    !emailPickerMode && (isBrokerageFirm || isBrokerageTeamMember || isOfficeSelection);
  const cardBadge = isOfficeSelection
    ? BADGE_MAP.OFFICE
    : isBrokerageTeamMember
      ? BADGE_MAP.BROKERAGE_TEAM_MEMBER
      : BADGE_MAP.BROKERAGE_FIRM;
  const officeCount = isOfficeSelection ? getOfficeAvailableCount(selectedItem) : offices.length;
  const selectedCompanyName =
    String(
      selectedItem?.company_name ||
        selectedItem?.brokerage_name ||
        selectedItem?.brokerage_firm_name ||
        ""
    ).trim() || "-";
  const selectedOfficeCount =
    selectedItem?.office_count ?? selectedItem?.offices_count ?? (officeCount > 0 ? officeCount : null);
  const selectedTeamCountText =
    selectedOfficeCount !== undefined &&
    selectedOfficeCount !== null &&
    String(selectedOfficeCount).trim() !== ""
      ? String(selectedOfficeCount).trim()
      : "-";

  // Select2 options: { value, label } for react-select
  const officeOptions = useMemo(
    () => offices.map((o) => ({ value: String(o.id), label: o.label || "—" })),
    [offices]
  );
  const teamMemberOptions = useMemo(
    () => teamMembers.map((m) => ({ value: String(m.id), label: m.label || "—", ...m })),
    [teamMembers]
  );
  const agentOptions = useMemo(
    () => agents.map((a) => ({ value: String(a.id), label: a.label || "—", ...a })),
    [agents]
  );
  const selectedOfficeOption = officeValue
    ? officeOptions.find((o) => o.value === officeValue) || null
    : null;
  const selectedOfficeName =
    String(
      selectedOfficeOption?.label ||
        selectedItem?.officeName ||
        selectedItem?.office_name ||
        selectedItem?.offices?.officeName ||
        ""
    ).trim() || "-";
  const selectedTeamMemberOption = teamMemberValue
    ? teamMemberOptions.find((m) => m.value === teamMemberValue) || null
    : null;
  const selectedAgentOption = agentValue
    ? agentOptions.find((a) => a.value === agentValue) || null
    : null;
  const showAgentSelect =
    (isBrokerageFirm || isBrokerageTeamMember || isOfficeSelection) && Boolean(selectedItem);

  const displayOfficeError = String(officeError || fieldErrors.office || "").trim();
  const displayTeamMemberError = String(teamMemberError || fieldErrors.teamMember || "").trim();

  const batSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 38,
      border: "1.5px solid #d1d5db",
      borderRadius: 8,
      fontSize: 13,
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      backgroundColor: state.isDisabled ? "#f8fafc" : base.backgroundColor,
      "&:hover": { borderColor: "#d1d5db" },
    }),
    valueContainer: (base, state) => ({
      ...base,
      cursor: state.isDisabled ? "not-allowed" : "default",
    }),
    indicatorsContainer: (base, state) => ({
      ...base,
      cursor: state.isDisabled ? "not-allowed" : "pointer",
    }),
    singleValue: (base, state) => ({
      ...base,
      cursor: state.isDisabled ? "not-allowed" : "default",
    }),
    menu: (base) => ({ ...base, zIndex: 20 }),
    menuList: (base) => ({ ...base, maxHeight: 240 }),
  };

  const getBatSelectStyles = (hasError) => ({
    ...batSelectStyles,
    control: (base, state) => ({
      ...batSelectStyles.control(base, state),
      borderColor: hasError ? "#dc3545" : state.isFocused ? "#86b7fe" : "#d1d5db",
      boxShadow: hasError
        ? "0 0 0 0.2rem rgba(220, 53, 69, 0.15)"
        : state.isFocused
          ? "0 0 0 0.2rem rgba(13, 110, 253, 0.25)"
          : "none",
      "&:hover": {
        borderColor: hasError ? "#dc3545" : "#86b7fe",
      },
    }),
  });

  // Load real offices for selected brokerage (reusing old office-select logic)
  useEffect(() => {
    const loadOffices = async () => {
      if (isOfficeSelection) {
        const brokerageOrgId = resolveOfficeSelectBrokerageOrganizationId(selectedItem);
        if (!brokerageOrgId || !token || !API_BASE) return;
        try {
          const url = `${API_BASE}${appendPermissionsFalse(`/organizations/${brokerageOrgId}/offices/select`)}`;
          const res = await fetch(url, {
            method: "POST",
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              page: 1,
              limit: 50,
              search: "",
            }),
          });
          if (!res.ok) {
            console.error("SearchBrokerageAgentTeam offices error:", res.statusText);
            return;
          }
          const json = await res.json();
          const list = json?.results || [];
          const opts = list.map((office) => ({
            id: office.id || office._id,
            label: office.officeName || office.name || "---",
          }));
          const wantedName = resolveOfficeLabelFromSearchItem(
            selectedItem,
            selectedItem?.label
          ).toLowerCase();
          const matchByName = wantedName
            ? opts.find((o) => String(o.label || "").trim().toLowerCase() === wantedName)
            : null;
          const currentOfficeValue = officeValueRef.current;
          const matchById = currentOfficeValue
            ? opts.find((o) => String(o.id) === currentOfficeValue)
            : null;
          const chosen = matchById || matchByName || (opts.length === 1 ? opts[0] : null);
          setOffices(opts);
          if (chosen?.id) {
            const nextId = String(chosen.id);
            setOfficeValue(nextId);
            onOfficeChangeRef.current?.({
              id: nextId,
              label: chosen.label || "—",
            });
          }
          setTeamMembers([]);
          setTeamMemberValue("");
        } catch (err) {
          console.error("SearchBrokerageAgentTeam offices error:", err);
        }
        return;
      }
      if ((!isBrokerageFirm && !isBrokerageTeamMember) || !selectedItem?.value || !token || !API_BASE) {
        setOffices([]);
        setOfficeValue("");
        return;
      }
      try {
        let res;
        if (isBrokerageFirm) {
          const brokerageOrgId = resolveOfficeSelectBrokerageOrganizationId(selectedItem);
          if (!brokerageOrgId) {
            setOffices([]);
            setOfficeValue("");
            return;
          }
          const url = `${API_BASE}${appendPermissionsFalse(`/organizations/${brokerageOrgId}/offices/select`)}`;
          res = await fetch(url, {
            method: "POST",
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              page: 1,
              limit: 50,
              search: "",
            }),
          });
        } else {
          // BROKERAGE_TEAM_MEMBER: assigned offices for membership
          const json = await fetchBrokerageTeamMemberOfficesSelect2(
            token,
            String(selectedItem.value),
            { page: 1, limit: 50 }
          );
          const list = json?.results || [];
          const opts = list.map((office) => ({
            id: office.id || office._id,
            label: office.officeName || office.name || "---",
          }));
          if (isBrokerageTeamMember && opts.length === 0 && officeValueRef.current) {
            return;
          }
          setOffices(opts);
          const currentOfficeValue = officeValueRef.current;
          const hasCurrentSelection = currentOfficeValue
            ? opts.some((o) => String(o.id) === currentOfficeValue)
            : false;
          if (hasCurrentSelection) {
            setOfficeValue(currentOfficeValue);
          } else if (isBrokerageTeamMember && opts.length > 0) {
            const firstOfficeId = String(opts[0]?.id || "").trim();
            setOfficeValue(firstOfficeId);
            if (firstOfficeId) {
              onOfficeChangeRef.current?.({
                id: firstOfficeId,
                label: opts[0]?.label || "—",
              });
            }
          } else {
            setOfficeValue("");
          }
          setTeamMembers([]);
          setTeamMemberValue("");
          return;
        }
        if (!res.ok) {
          console.error("SearchBrokerageAgentTeam offices error:", res.statusText);
          // Keep prefilled office when list endpoint fails (project restore / team-member).
          if ((isBrokerageTeamMember || isBrokerageFirm) && officeValueRef.current) return;
          setOffices([]);
          setOfficeValue("");
          return;
        }
        const json = await res.json();
        const list = json?.results || [];
        const opts = list.map((office) => ({
          id: office.id || office._id,
          label: office.officeName || office.name || "---",
        }));
        if ((isBrokerageTeamMember || isBrokerageFirm) && opts.length === 0 && officeValueRef.current) {
          return;
        }
        const currentOfficeValue = officeValueRef.current;
        const hasCurrentSelection = currentOfficeValue
          ? opts.some((o) => String(o.id) === currentOfficeValue)
          : false;
        // Preserve a project-restored office that is missing from the page-1 list.
        const mergedOpts =
          currentOfficeValue && !hasCurrentSelection
            ? [
                {
                  id: currentOfficeValue,
                  label:
                    officesListRef.current.find((o) => String(o.id) === currentOfficeValue)?.label ||
                    "Office",
                },
                ...opts,
              ]
            : opts;
        setOffices(mergedOpts);
        if (currentOfficeValue && mergedOpts.some((o) => String(o.id) === currentOfficeValue)) {
          setOfficeValue(currentOfficeValue);
        } else if (isBrokerageTeamMember && mergedOpts.length > 0) {
          const firstOfficeId = String(mergedOpts[0]?.id || "").trim();
          setOfficeValue(firstOfficeId);
          if (firstOfficeId) {
            onOfficeChangeRef.current?.({
              id: firstOfficeId,
              label: mergedOpts[0]?.label || "—",
            });
          }
        } else {
          setOfficeValue("");
        }
        setTeamMembers([]);
        setTeamMemberValue("");
      } catch (err) {
        console.error("SearchBrokerageAgentTeam offices error:", err);
        if ((isBrokerageTeamMember || isBrokerageFirm) && officeValueRef.current) return;
        setOffices([]);
        setOfficeValue("");
      }
    };
    loadOffices();
  }, [isBrokerageFirm, isBrokerageTeamMember, isOfficeSelection, selectedItem?.value, selectedItem?.organizationId, selectedItem?.label, token]);

  // Team-member flow: keep office prefilled when only office data is available.
  useEffect(() => {
    if (!isBrokerageTeamMember) return;
    if (officeValue) return;
    if (!Array.isArray(offices) || offices.length === 0) return;
    const firstOffice = offices[0];
    const officeId = String(firstOffice?.id || "").trim();
    if (!officeId) return;
    setOfficeValue(officeId);
    onOfficeChangeRef.current?.({
      id: officeId,
      label: firstOffice?.label || "—",
    });
  }, [isBrokerageTeamMember, officeValue, offices]);

  // Load real team members for selected office (reusing old team-member-select logic)
  useEffect(() => {
    const loadTeamMembers = async () => {
      if (!officeValue || !token || !API_BASE) {
        setTeamMembers([]);
        setTeamMemberValue("");
        return;
      }
      try {
        const json = await fetchOfficeTeamMembersSelect2(token, officeValue, {
          page: 1,
          limit: 20,
        });
        const list = Array.isArray(json?.results) ? json.results : [];
        const opts = list
          .map((u) => {
            const mapped = mapFoundationMembershipRowToOption(u);
            return {
              id: mapped.membershipId,
              membershipId: mapped.membershipId,
              globalUserId: mapped.globalUserId,
              organizationId: mapped.organizationId,
              addressOwnerId: String(officeValue || "").trim(),
              label: mapped.label,
              firstName: mapped.firstName,
              lastName: mapped.lastName,
              email: mapped.email,
              phone: resolvePrimaryPhoneFromUserLike(u),
            };
          })
          .filter((m) => m.id);
        setTeamMembers(opts);
        setTeamMemberValue((prev) => {
          const prevId = String(prev || "").trim();
          if (prevId && opts.some((m) => String(m.id) === prevId)) return prevId;
          return "";
        });
      } catch (err) {
        console.error("SearchBrokerageAgentTeam team-members error:", err);
        setTeamMembers([]);
        setTeamMemberValue("");
      }
    };
    loadTeamMembers();
  }, [officeValue, token]);

  useEffect(() => {
    const loadAgents = async () => {
      if (!showAgentSelect || !officeValue || !token || !API_BASE) {
        setAgents([]);
        setAgentValue("");
        return;
      }

      try {
        setAgentsLoading(true);
        const q = String(agentQuery || "").trim();
        const json = await fetchBrokerageAgentsByOfficeIdsSelect2(token, officeValue, {
          page: 1,
          limit: 50,
          q,
        });
        const list = Array.isArray(json?.results) ? json.results : [];
        const opts = list
          .map((u) => {
            const mapped = mapFoundationMembershipRowToOption(u, {
              officeId: officeValue,
            });
            return {
              id: mapped.membershipId,
              membershipId: mapped.membershipId,
              globalUserId: mapped.globalUserId,
              organizationId: mapped.organizationId,
              addressOwnerId: mapped.organizationId,
              label: mapped.label,
              firstName: mapped.firstName,
              lastName: mapped.lastName,
              email: mapped.email,
              phone: resolvePrimaryPhoneFromUserLike(u),
            };
          })
          .filter((u) => u.id);
        setAgents(opts);
      } catch (err) {
        console.error("SearchBrokerageAgentTeam agents error:", err);
        setAgents([]);
      } finally {
        setAgentsLoading(false);
      }
    };

    const timer = window.setTimeout(loadAgents, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [showAgentSelect, officeValue, token, agentQuery]);

  const restoreContactAfterAgentClear = () => {
    const member = selectedTeamMemberOption;
    if (member) {
      const nextContact = {
        fName: member.firstName || "",
        lName: member.lastName || "",
        email: member.email || "",
        phone: applyDefaultPhoneIfEmpty(member.phone),
      };
      setContact(nextContact);
      onContactChangeRef.current?.(nextContact);
      return;
    }

    if (isBrokerageTeamMember && selectedItem) {
      const displayName = selectedItem.label || "";
      const parts = displayName.split(" ");
      const nextContact = {
        fName: parts[0] || selectedItem.firstName || "",
        lName: parts.slice(1).join(" ") || selectedItem.lastName || "",
        email: selectedItem.email || "",
        phone: applyDefaultPhoneIfEmpty(resolvePrimaryPhoneFromUserLike(selectedItem)),
      };
      setContact(nextContact);
      onContactChangeRef.current?.(nextContact);
      return;
    }

    const empty = { fName: "", lName: "", email: "", phone: "" };
    setContact(empty);
    onContactChangeRef.current?.(empty);
  };

  const officesListRef = useRef([]);
  const teamMembersListRef = useRef([]);
  const agentsListRef = useRef([]);
  useEffect(() => {
    officesListRef.current = offices;
  }, [offices]);
  useEffect(() => {
    teamMembersListRef.current = teamMembers;
  }, [teamMembers]);
  useEffect(() => {
    agentsListRef.current = agents;
  }, [agents]);

  const runSelectionValidation = useCallback((overrides = {}) => {
    if (readOnly) {
      return { valid: true, errors: {} };
    }
    const item = selection?.item;
    if (!item) {
      const errors = { office: "", teamMember: "" };
      setFieldErrors(errors);
      return { valid: false, errors: { search: "Search and select a brokerage, office, agent, or team member." } };
    }

    const officeId = String(overrides.officeId ?? officeValueRef.current ?? "").trim();
    const teamMemberId = String(overrides.teamMemberId ?? teamMemberValueRef.current ?? "").trim();

    const result = validateSearchBrokerageAgentTeamSelection({
      licenseType: getEffectiveLicenseType(item),
      officeId,
      teamMemberId,
    });
    setFieldErrors({
      office: result.errors.office || "",
      teamMember: result.errors.teamMember || "",
    });
    return result;
  }, [readOnly, selection]);

  useImperativeHandle(ref, () => ({
    reset: () => {
      clearAll();
    },
    restoreApplyPick: (item) => {
      pickLatestRef.current?.(item);
    },
    restoreSyncContact: (contact) => {
      const next = {
        fName: contact?.fName ?? "",
        lName: contact?.lName ?? "",
        email: contact?.email ?? "",
        phone: applyDefaultPhoneIfEmpty(contact?.phone),
      };
      setContact(next);
      onContactChangeRef.current?.(next);
    },
    restoreSelectOfficeById: async (officeId, fallbackLabel) => {
      const id = String(officeId ?? "").trim();
      if (!id) return false;
      const label = String(fallbackLabel || "").trim() || "Office";

      const applyOffice = (officeLabel) => {
        setOffices((prev) => {
          if (prev.some((o) => String(o.id) === id)) return prev;
          return [...prev, { id, label: officeLabel }];
        });
        setOfficeValue(id);
        setTeamMembers([]);
        setTeamMemberValue("");
        onOfficeChangeRef.current?.({ id, label: officeLabel });
      };

      // Prefer immediate apply when caller already knows the office (project restore).
      if (fallbackLabel) {
        const existing = officesListRef.current.find((o) => String(o.id) === id);
        applyOffice(existing?.label || label);
        return true;
      }

      const deadline = Date.now() + 12000;
      while (Date.now() < deadline) {
        const office = officesListRef.current.find((o) => String(o.id) === id);
        if (office) {
          setOfficeValue(id);
          setTeamMembers([]);
          setTeamMemberValue("");
          onOfficeChangeRef.current?.({ id: String(office.id), label: office.label || "—" });
          return true;
        }
        await new Promise((r) => setTimeout(r, 90));
      }
      applyOffice(label);
      return true;
    },
    restoreSelectTeamMemberById: async (memberId) => {
      const id = String(memberId ?? "").trim();
      if (!id) return false;
      const deadline = Date.now() + 12000;
      while (Date.now() < deadline) {
        const member = teamMembersListRef.current.find((m) => String(m.id) === id);
        if (member) {
          setTeamMemberValue(id);
          const nextContact = {
            fName: member.firstName,
            lName: member.lastName,
            email: member.email,
            phone: applyDefaultPhoneIfEmpty(member.phone),
          };
          setContact(nextContact);
          onContactChangeRef.current?.(nextContact);
          onTeamMemberChangeRef.current?.({
            id: String(member.id),
            membershipId: String(member.membershipId ?? member.id),
            globalUserId: String(member.globalUserId ?? ""),
            organizationId: String(member.organizationId ?? ""),
            addressOwnerId: String(
              member.addressOwnerId ?? officeValueRef.current ?? member.globalUserId ?? member.id
            ),
            label: member.label,
            firstName: member.firstName,
            lastName: member.lastName,
            email: member.email,
            phone: applyDefaultPhoneIfEmpty(member.phone),
          });
          return true;
        }
        await new Promise((r) => setTimeout(r, 90));
      }
      return false;
    },
    restoreSelectAgentById: async (agentId, fallback) => {
      const id = String(agentId ?? "").trim();
      if (!id) return false;

      const applyAgent = (agent) => {
        setAgentValue(id);
        const nextContact = {
          customer_id: String(agent.id),
          fName: agent.firstName,
          lName: agent.lastName,
          email: agent.email,
          phone: applyDefaultPhoneIfEmpty(agent.phone),
        };
        setContact(nextContact);
        onContactChangeRef.current?.(nextContact);
        const membershipId = String(agent.membershipId ?? fallback?.membershipId ?? agent.id ?? id).trim();
        const globalUserId = String(agent.globalUserId ?? fallback?.globalUserId ?? "").trim();
        const organizationId = String(agent.organizationId ?? fallback?.organizationId ?? "").trim();
        onAgentChangeRef.current?.({
          id: String(agent.id),
          membershipId,
          globalUserId,
          organizationId,
          addressOwnerId: String(
            agent.addressOwnerId ?? organizationId ?? globalUserId ?? membershipId
          ),
          label: agent.label,
          firstName: agent.firstName,
          lastName: agent.lastName,
          email: agent.email,
          phone: applyDefaultPhoneIfEmpty(agent.phone),
        });
      };

      const deadline = Date.now() + 12000;
      while (Date.now() < deadline) {
        const agent = agentsListRef.current.find((a) => String(a.id) === id);
        if (agent) {
          applyAgent(agent);
          return true;
        }
        await new Promise((r) => setTimeout(r, 90));
      }

      const label =
        String(fallback?.label || "").trim() ||
        [fallback?.firstName, fallback?.lastName].filter(Boolean).join(" ").trim();
      if (!label) return false;

      const synthetic = {
        id,
        membershipId: String(fallback?.membershipId || id).trim() || id,
        globalUserId: String(fallback?.globalUserId || "").trim(),
        organizationId: String(fallback?.organizationId || "").trim(),
        addressOwnerId: String(
          fallback?.organizationId || fallback?.globalUserId || id
        ).trim(),
        label,
        firstName: String(fallback?.firstName || label.split(/\s+/)[0] || "").trim(),
        lastName: String(fallback?.lastName || label.split(/\s+/).slice(1).join(" ") || "").trim(),
        email: String(fallback?.email || "").trim(),
        phone: String(fallback?.phone || "").trim(),
      };
      setAgents((prev) => (prev.some((a) => String(a.id) === id) ? prev : [...prev, synthetic]));
      applyAgent(synthetic);
      return true;
    },
    validate: (overrides) => runSelectionValidation(overrides || {}),
  }), [runSelectionValidation]);

  return (
    <div
      className={`search-bat-wrap${isProjectCreateUi ? " search-bat--project-create" : ""}${isSearchLocked ? " search-bat--readonly" : ""}`}
    >
      <div className="search-bat-inner">
        {/* <h1 className="search-bat-title">{title}</h1>
        <p className="search-bat-sub">{subtitle}</p> */}
        <div className={isProjectCreateUi ? "search-bat-embedded" : "search-bat-card"}>
          {!isProjectCreateUi && <div className="search-bat-card-head" />}
          <div
            className={`search-bat-body${isProjectCreateUi ? " search-bat-body--embedded" : ""}`}
          >
            <div className="search-bat-search-wrap" ref={dropdownRef}>
              {!isProjectCreateUi && (
                <label className="serach-tittle">
                  Search by Brokerage, Office, Agent, or Admin Name{" "}
                  <span className="search-bat-req">*</span>
                </label>
              )}
              <form
                className="search-bat-search-box"
                autoComplete="off"
                onSubmit={(e) => {
                  e.preventDefault();
                }}
              >
                <span className="search-bat-search-icon" aria-hidden>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#9ca3af"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </span>
                <input
                  type="search"
                  id="search-bat-input"
                  name="brokerage_agent_team_filter"
                  className="search-bat-search-input"
                  placeholder={searchInputPlaceholder}
                  value={searchInput}
                  onChange={isSearchLocked ? undefined : handleSearchChange}
                  onFocus={isSearchLocked ? undefined : handleSearchFocus}
                  onKeyDown={isSearchLocked ? undefined : handleSearchKeyDown}
                  readOnly={isSearchLocked}
                  disabled={isSearchLocked}
                  autoComplete="off"
                  data-lpignore="true"
                />
                <button
                  type="button"
                  className="search-bat-clear-btn"
                  onClick={isSearchLocked ? undefined : clearAll}
                  aria-label="Clear"
                  disabled={isSearchLocked}
                >
                  ×
                </button>
              </form>
              {/* {hintsVisible && (
                <div className="search-bat-hints">
                  <button type="button" className="search-bat-hint" onClick={() => setQuery("")}>
                    Show all
                  </button>
                </div>
              )} */}
              {!isSearchLocked && (
                <div
                  className={`search-bat-dropdown ${!dropdownVisible ? "search-bat-hidden" : ""}`}
                  id="search-bat-dropdown"
                  ref={dropdownListRef}
                  onScroll={handleDropdownScroll}
                >
                {apiResults.length === 0 ? (
                  loading ? (
                    <div
                      style={{
                        padding: "20px",
                        textAlign: "center",
                        color: "#7B8BAA",
                        fontSize: "13px",
                      }}
                    >
                      Loading…
                    </div>
                  ) : (
                    <div
                      style={{
                        padding: "20px",
                        textAlign: "center",
                        color: "#7B8BAA",
                        fontSize: "13px",
                      }}
                    >
                      {!organizationId ? "No results found" : "No results found"}
                    </div>
                  )
                ) : (
                  <>
                    {apiResults.map((item, index) => {
                      const effLt = getEffectiveLicenseType(item);
                      const badge = BADGE_MAP[effLt] || BADGE_MAP.AGENT;
                      const meta = getMeta(item);
                      const metaLines = getMetaLines(item);
                      const bg =
                        effLt === "BROKERAGE_FIRM"
                          ? "#EEF3FF"
                          : effLt === "OFFICE"
                            ? "#F3E8FF"
                            : effLt === "AGENT" || item.license_type === "BROKERAGE_AGENT"
                              ? "#EDFAF4"
                              : "#FFF4E6";
                      const rowKey = `${String(item._id ?? item.id ?? item.value ?? "row")}-${effLt}-${index}`;
                      return (
                        <React.Fragment key={rowKey}>
                          <div
                            className="search-bat-drop-row"
                            onClick={() => pick(item)}
                            onKeyDown={(e) => e.key === "Enter" && pick(item)}
                            role="button"
                            tabIndex={0}
                          >
                            <div
                              className="search-bat-drop-icon"
                              // style={{ background: bg }}
                            >
                              <img
                                src={(item.profile_pic_url || item.logo_url) || imageCustomSquare}
                                alt=""
                                className="search-bat-drop-img"
                                onError={handleProfileImageError}
                              />
                            </div>
                            <div className="search-bat-drop-main">
                              <div className="search-bat-drop-text">
                                <div className="search-bat-drop-name">{getPrimaryDisplayText(item)}</div>
                                {Array.isArray(metaLines) ? (
                                  <>
                                    <div className="search-bat-drop-meta">{metaLines[0] || "-"}</div>
                                    <div className="search-bat-drop-meta">{metaLines[1] || "-"}</div>
                                  </>
                                ) : isMeaningfulMeta(meta) ? (
                                  <div className="search-bat-drop-meta">{meta}</div>
                                ) : null}
                                <div className="search-bat-drop-email">{formatEmailForRow(item.email)}</div>
                              </div>
                              <span className={badge.cls}>{badge.label.toUpperCase()}</span>
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })}
                    {loading && (
                      <div
                        style={{
                          padding: "10px 14px 14px",
                          textAlign: "center",
                          color: "#7B8BAA",
                          fontSize: "12px",
                        }}
                      >
                        Loading more…
                      </div>
                    )}
                  </>
                )}
              </div>
              )}
            </div>

            {showBrokerageCard && (
              <div className="search-bat-brok-card" id="search-bat-brok-card">
                <div className="search-bat-brok-top">
                  <span style={{ fontSize: "24px" }}>
                    <img
                      src={(selectedItem?.profile_pic_url || selectedItem?.logo_url) || imageCustomSquare}
                      alt=""
                      style={{ width: 50, height: 50, borderRadius: 10, objectFit: "contain" }}
                      onError={handleProfileImageError}
                    />
                  </span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "14px", color: "#0F1726",fontFamily: 'Roboto, sans-serif' }}>
                      {selectedItem?.displayLabel || selectedItem?.label || ""}
                    </div>
                    {isOfficeSelection && selectedItem?.company_name && (
                      <div style={{ fontSize: "12px", color: "#7B8BAA" ,fontFamily: 'Roboto, sans-serif'}}>
                        {selectedItem.company_name}
                      </div>
                    )}
                    <div style={{ fontSize: "12px", color: "#7B8BAA" ,fontFamily: 'Roboto, sans-serif'}}>
                      {isBrokerageTeamMember
                        ? `${selectedCompanyName}, ${selectedTeamCountText}`
                        : `${selectedCompanyName}, ${selectedOfficeName}`}
                    </div>
                  </div>
                  <span className={cardBadge.cls} style={{ marginLeft: "auto" }}>
                    {cardBadge.label.toUpperCase()}
                  </span>
                </div>
                <div className="search-bat-grid2">
                  <div>
                    <label className="search-bat-label">
                      Select Office <span className="search-bat-req">*</span>
                    </label>
                    <Select
                      className="search-bat-select2"
                      classNamePrefix="search-bat-select2"
                      options={officeOptions}
                      value={selectedOfficeOption}
                      placeholder="Select office…"
                      isSearchable
                      isClearable
                      isDisabled={isSearchLocked}
                      styles={getBatSelectStyles(Boolean(displayOfficeError))}
                      onChange={(opt) => {
                        const nextId = opt?.value ?? "";
                        setOfficeValue(nextId);
                        setFieldErrors((prev) => ({ ...prev, office: "" }));
                        setTeamMembers([]);
                        setTeamMemberValue("");
                        setAgentValue("");
                        setAgentQuery("");
                        setAgents([]);
                        onAgentChangeRef.current?.(null);
                        if (nextId && typeof onOfficeChange === "function") {
                          const office = offices.find((o) => String(o.id) === nextId) || null;
                          if (office) onOfficeChange(office);
                        }
                      }}
                    />
                    {displayOfficeError ? (
                      <div className="search-bat-field-error">{displayOfficeError}</div>
                    ) : null}
                  </div>
                  {(isBrokerageFirm || isOfficeSelection) && (
                    <div>
                      <label className="search-bat-label">
                        Team Member
                        {/* <span className="search-bat-req">*</span> */}
                      </label>
                      <Select
                        className="search-bat-select2"
                        classNamePrefix="search-bat-select2"
                        options={teamMemberOptions}
                        value={selectedTeamMemberOption}
                        placeholder="Select member…"
                        isSearchable
                        isClearable
                        isDisabled={isSearchLocked}
                        styles={getBatSelectStyles(Boolean(displayTeamMemberError))}
                        onChange={(opt) => {
                          const nextId = opt?.value ?? "";
                          setTeamMemberValue(nextId);
                          setFieldErrors((prev) => ({ ...prev, teamMember: "" }));
                          if (!nextId) {
                            onTeamMemberChangeRef.current?.(null);
                            return;
                          }
                          const member = teamMembers.find((m) => String(m.id) === nextId) || null;
                          if (member) {
                            const nextContact = {
                              fName: member.firstName,
                              lName: member.lastName,
                              email: member.email,
                              phone: applyDefaultPhoneIfEmpty(member.phone),
                            };
                            // Only overwrite contact from team member when no agent is selected.
                            if (!agentValue) {
                              setContact(nextContact);
                              onContactChangeRef.current?.(nextContact);
                            }
                            onTeamMemberChangeRef.current?.({
                              ...member,
                              phone: applyDefaultPhoneIfEmpty(member.phone),
                            });
                          }
                        }}
                      />
                      {displayTeamMemberError ? (
                        <div className="search-bat-field-error">{displayTeamMemberError}</div>
                      ) : null}
                    </div>
                  )}
                  {showAgentSelect && (
                    <div>
                      <label className="search-bat-label">
                        Select Agent 
                        {/* <span className="text-muted">(optional)</span> */}
                      </label>
                      <Select
                        className="search-bat-select2"
                        classNamePrefix="search-bat-select2"
                        options={agentOptions}
                        value={selectedAgentOption}
                        placeholder={officeValue ? "Select agent…" : "Select office first…"}
                        isSearchable
                        isClearable
                        isLoading={agentsLoading}
                        isDisabled={isSearchLocked || !officeValue}
                        styles={batSelectStyles}
                        onInputChange={(value, actionMeta) => {
                          if (actionMeta.action === "input-change") {
                            setAgentQuery(value);
                          }
                        }}
                        onChange={(opt) => {
                          const nextId = opt?.value ?? "";
                          setAgentValue(nextId);

                          if (!nextId) {
                            onAgentChangeRef.current?.(null);
                            restoreContactAfterAgentClear();
                            return;
                          }

                          const agent = agents.find((a) => String(a.id) === nextId) || null;
                          if (!agent) return;

                          const nextContact = {
                            customer_id: String(agent.id),
                            fName: agent.firstName,
                            lName: agent.lastName,
                            email: agent.email,
                            phone: applyDefaultPhoneIfEmpty(agent.phone),
                          };
                          setContact(nextContact);
                          onContactChangeRef.current?.(nextContact);
                          onAgentChangeRef.current?.({
                            ...agent,
                            phone: applyDefaultPhoneIfEmpty(agent.phone),
                          });
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {selection && !emailPickerMode && (
              <>
                <div className="search-bat-divider">
                  <div className="search-bat-divider-line" />
                  <span className="search-bat-divider-text">Contact Information</span>
                  <div className="search-bat-divider-line" />
                </div>
                <div className="search-bat-grid2">                 
                <div>
                <label className="search-bat-label">Brokerage Company Name</label>
                    <input
                      type="text"
                      id="search-bat-brokerage-company-name"
                      className="search-bat-input"
                      value={selectedCompanyName}
                      disabled
                      readOnly
                    />
                  </div>
                  <div>
                  <label className="search-bat-label">Office Name</label>
                    <input
                      type="text"
                      id="search-bat-office-name"
                      className="search-bat-input"
                      value={selectedOfficeName}
                      disabled
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="search-bat-label">First Name</label>
                    <input
                      type="text"
                      id="search-bat-fName"
                      className="search-bat-input"
                      value={contact.fName}
                      disabled={contactReadOnly || contactNameReadOnly}
                      readOnly={contactReadOnly || contactNameReadOnly}
                      onChange={(e) => {
                        const next = { ...contact, fName: e.target.value };
                        setContact(next);
                        if (typeof onContactChange === "function") onContactChange(next);
                      }}
                    />
                  </div>
                  <div>
                    <label className="search-bat-label">Last Name</label>
                    <input
                      type="text"
                      id="search-bat-lName"
                      className="search-bat-input"
                      value={contact.lName}
                      disabled={contactReadOnly || contactNameReadOnly}
                      readOnly={contactReadOnly || contactNameReadOnly}
                      onChange={(e) => {
                        const next = { ...contact, lName: e.target.value };
                        setContact(next);
                        if (typeof onContactChange === "function") onContactChange(next);
                      }}
                    />
                  </div>
                  <div>
                    <label className="search-bat-label">
                      Email <span className="search-bat-req">*</span>
                    </label>
                    <input
                      type="email"
                      id="search-bat-email"
                      className={`search-bat-input ${emailError ? "is-invalid" : ""}`}
                      value={contact.email}
                      disabled={contactReadOnly}
                      style={emailError ? { borderColor: "#dc3545" } : undefined}
                      onChange={(e) => {
                        const next = { ...contact, email: e.target.value };
                        setContact(next);
                        if (typeof onContactChange === "function") onContactChange(next);
                      }}
                    />
                    {emailError ? <div className="text-danger mt-1">{emailError}</div> : null}
                  </div>
                  <div>
                    <label className="search-bat-label">
                      Phone <span className="search-bat-req">*</span>
                    </label>
                    <input
                      type="tel"
                      id="search-bat-phone"
                      className={`search-bat-input ${phoneError ? "is-invalid" : ""}`}
                      value={phoneForInput(contact.phone)}
                      placeholder={DEFAULT_TEN_ZEROS}
                      disabled={contactReadOnly}
                      style={phoneError ? { borderColor: "#dc3545" } : undefined}
                      onChange={(e) => {
                        const next = { ...contact, phone: e.target.value };
                        setContact(next);
                        onContactChangeRef.current?.({
                          ...next,
                          phone: applyDefaultPhoneIfEmpty(next.phone),
                        });
                      }}
                    />
                    {phoneError ? <div className="text-danger mt-1">{phoneError}</div> : null}
                  </div>
                </div>
                
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default SearchBrokerageAgentTeam;
